import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  login as loginRequest,
  registerAccount as registerRequest,
  fetchSession,
  type UserProfile,
} from "../api/auth";
import { storePaywallPayload, type PaywallPayload } from "../api/billing";
import {
  clearSession,
  getStoredProfile,
  getStoredToken,
  setStoredProfile,
  setStoredToken,
  setUnauthorizedHandler,
} from "../api/client";
import { ApiClientError } from "../api/types";
import { normalizeRoleCodes } from "../lib/navigation";
import { hasRole, type RoleCodeValue } from "../lib/rbac";

function withNormalizedRoles(profile: UserProfile): UserProfile {
  return { ...profile, roles: normalizeRoleCodes(profile.roles) };
}

interface AuthContextValue {
  token: string | null;
  profile: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (login: string, password: string, orgId?: string) => Promise<void>;
  registerAccount: (payload: {
    companyName: string;
    email: string;
    whatsapp: string;
    password: string;
    confirmPassword: string;
    provisionKey?: string;
  }) => Promise<void>;
  logout: () => void;
  hasRole: (...roles: RoleCodeValue[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function persistSession(token: string, profile: UserProfile) {
  setStoredToken(token);
  setStoredProfile(profile);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getStoredToken());
  const [profile, setProfile] = useState<UserProfile | null>(() => {
    const stored = getStoredProfile<UserProfile>();
    return stored ? withNormalizedRoles(stored) : null;
  });
  const [isLoading, setIsLoading] = useState(() => Boolean(getStoredToken()));

  const logout = useCallback(() => {
    clearSession();
    setToken(null);
    setProfile(null);
  }, []);

  const redirectPaywall = useCallback(
    (paywall: PaywallPayload, checkoutPassword?: string) => {
      clearSession();
      setToken(null);
      setProfile(null);
      storePaywallPayload(paywall, checkoutPassword);
      window.location.assign("/pay");
    },
    [],
  );

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setToken(null);
      setProfile(null);
    });

    async function verifySession() {
      if (!getStoredToken()) {
        setIsLoading(false);
        return;
      }
      try {
        const res = await fetchSession();
        setProfile(withNormalizedRoles(res.profile));
        setToken(getStoredToken());
      } catch (err) {
        if (
          err instanceof ApiClientError &&
          err.reason === "subscription_expired" &&
          err.paywall
        ) {
          redirectPaywall(err.paywall as PaywallPayload);
        } else if (
          err instanceof ApiClientError &&
          err.reason === "session_replaced"
        ) {
          logout();
        } else {
          clearSession();
          setToken(null);
          setProfile(null);
        }
      } finally {
        setIsLoading(false);
      }
    }

    void verifySession();

    const poll = window.setInterval(() => {
      if (!getStoredToken()) return;
      void fetchSession()
        .then((res) => setProfile(withNormalizedRoles(res.profile)))
        .catch((err) => {
          if (
            err instanceof ApiClientError &&
            err.reason === "session_replaced"
          ) {
            logout();
          }
        });
    }, 45_000);

    return () => window.clearInterval(poll);
  }, [redirectPaywall, logout]);

  const applyAuthSuccess = useCallback(
    (tokenValue: string, profileValue: UserProfile) => {
      const profile = withNormalizedRoles(profileValue);
      persistSession(tokenValue, profile);
      setToken(tokenValue);
      setProfile(profile);
    },
    [],
  );

  const handleLogin = useCallback(
    async (loginId: string, password: string, orgId?: string) => {
      try {
        const res = await loginRequest({
          login: loginId.trim().toLowerCase(),
          password,
          orgId: orgId?.trim() || undefined,
        });
        applyAuthSuccess(res.token, res.profile);
      } catch (err) {
        if (
          err instanceof ApiClientError &&
          err.reason === "subscription_expired" &&
          err.paywall
        ) {
          redirectPaywall(err.paywall as PaywallPayload, password);
          return;
        }
        throw err;
      }
    },
    [applyAuthSuccess, redirectPaywall],
  );

  const handleRegister = useCallback(
    async (payload: {
      companyName: string;
      email: string;
      whatsapp: string;
      password: string;
      confirmPassword: string;
      provisionKey?: string;
    }) => {
      const res = await registerRequest(payload);
      applyAuthSuccess(res.token, res.profile);
    },
    [applyAuthSuccess],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      profile,
      isAuthenticated: Boolean(token && profile),
      isLoading,
      login: handleLogin,
      registerAccount: handleRegister,
      logout,
      hasRole: (...roles: RoleCodeValue[]) =>
        profile ? hasRole(profile.roles, ...roles) : false,
    }),
    [token, profile, isLoading, handleLogin, handleRegister, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}

export function getLoginErrorMessage(err: unknown): string {
  if (err instanceof ApiClientError) {
    return err.message;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return "حدث خطأ. حاول مرة أخرى.";
}
